// One-shot self-heal for a stale deploy: a page or bundle cached before a
// deploy asks for chunks or module IDs the new build lacks. Reload once (the
// sessionStorage gate prevents loops) to pick up the fresh asset URLs.
window.addEventListener('unhandledrejection', event => {
  const reason = event.reason;
  const message = String(reason?.message || '');
  const looksLikeStaleDeploy =
    reason?.name === 'ChunkLoadError' ||
    (/undefined/i.test(message) &&
      (/reading\s+['"]call['"]/i.test(message) || /\.call\b/.test(message)));
  if (looksLikeStaleDeploy && !sessionStorage.getItem('cacheBustReloaded')) {
    sessionStorage.setItem('cacheBustReloaded', '1');
    location.reload();
  }
});

//global
import '@babel/polyfill';
import * as Sentry from '@sentry/browser';
import objectFitImages from 'object-fit-images';
import modalPanel from './components/modal-panel';
import MicroModal from 'micromodal';
import inPageNav from './modules/in-page-nav';
import globalNavDesktop from './modules/global-nav-desktop';
import formLabelAnimation from './components/form-label-animation';
import formFindRep from './components/form-find-rep';
import formContactRep from './components/form-contact-rep';
import accordion from './modules/accordion';
import {
  hasClass,
  removeClass,
  videoPlayerHandler,
  goToCurrentScroll,
} from './helpers/helpers';
import {
  adobeAnalyticsPageLoad,
  fireAdobeAnalyticsEvent,
  adobeCleanString,
} from './helpers/adobe-analytics';
import EventEmitter2 from 'eventemitter2';
import { showWebChatOnClick } from './helpers/talkdesk-chat';
import { resetDropdown } from './helpers/accessibility';
//modules
import fullSwiper from './components/carousel-full';
import threeUpSwiper from './components/carousel-driver-3up';
import twoUpSwiper from './components/carousel-driver-2up';
import heroSwiper from './components/carousel-hero';
import materialsSwiper from './components/carousel-materials';
import swatchSwiper from './components/carousel-swatches';
import contentBlockSwiper from './components/carousel-content-block';
import imageGallerySwiper from './components/carousel-image-gallery';
import videoSwiper from './components/carousel-videos';
// import storiesSwiper from './components/carousel-stories';
import imageGrid from './modules/image-grid';
import filter from './modules/filter';
import plpFilter from './modules/plp-filter';
import pcpColorFilter from './modules/pcp-color-filter';
import expandAll from './modules/color-expand-all';
import plpToggle from './modules/plp-toggle';
import pdpSwatchList from './modules/pdp-swatch-list';
import analyticsDownloadTracker from './modules/analytics-download-tracker';
import infoCardDropdown from './modules/info-card-dropdown';
import updateSearchHeading from './components/site-search';
import multipleFileUpload from './multiple-file-upload';
import contestSubmissionModals from './contest-submission-modals';
import prettySelect from './components/pretty-select';
import sampleTypesSelect from './components/sample-types-select';
import setupVisualizationEventListener from './components/bitreel-handlers/visualizer';
import setupDigitalPrintEventListener from './components/bitreel-handlers/digital-print';
import threeformSession from './modules/threeform-session';
import { trackEmbeddedVideos } from './helpers/embedded-video-analytics';
import './components/nav-panel';
import './components/global-footer';
import './components/global-search';
import './components/add-to-cart-modal';
import { isMobileOrTablet } from './helpers/layout';

// Need to keep this for the cart items count to display properly
import {
  spreeIntegration,
  loadAccountInformation,
  setupAccountInformationSync,
} from '@Root/spree';

window.EVT = new EventEmitter2();
window.Sentry = Sentry;
// MicroModal is referenced as a bare global from modal-panel.js, spree/api.js,
// and several Vue components; expose it on window so we can drop the jsdelivr
// CDN tag without touching every callsite.
window.MicroModal = MicroModal;

// JS helper for accessible cards with selectable text
// https://inclusive-components.design/cards/
window.initAccessibleCards = () => {
  const cards = document.querySelectorAll('.js-accessible-card');
  Array.prototype.forEach.call(cards, card => {
    //console.log('running');
    let down,
      up,
      link = card.querySelector('.js-accessible-link');
    card.onmousedown = () => {
      //console.log('mousedown');
      return (down = +new Date());
    };
    card.onmouseup = () => {
      up = +new Date();
      if (up - down < 200 && link) {
        link.click();
      }
    };
  });
};

window.goToSlide = (index = null, idx) => {
  const isSwatchCarousel =
    document.querySelector('.js-swiper-swatches') !== null;
  const swiperId = isSwatchCarousel
    ? `swatch-carousel-${index}`
    : 'video-carousel';
  const modalId = isSwatchCarousel
    ? `swatch-modal-${index}`
    : 'video-modal-panel';
  const modalClose = isSwatchCarousel
    ? `.js-modal-close-trigger-${index}`
    : '.js-video-modal-close-trigger';

  let mySwiper = document.getElementById(swiperId).swiper;
  mySwiper.slideTo(idx, 0);
  MicroModal.show(modalId, {
    onClose: modal => {
      goToCurrentScroll(false);
      // handle pausing videos in carousel
      if (modal.querySelector('.js-swiper-videos')) {
        videoPlayerHandler(`#${swiperId}`);
      }
    },
  });
  goToCurrentScroll(true);
  document.querySelector(modalClose).addEventListener('click', event => {
    event.preventDefault();
    MicroModal.close(modalId);
  });
};

if (document.readyState !== 'loading') {
  // Workaround for a Safari iOS bug regarding timing of 'DOMContentLoaded'
  // https://developer.apple.com/forums/thread/651215
  runOnPageLoad();
} else {
  document.addEventListener('DOMContentLoaded', function() {
    runOnPageLoad();
  });
}

// Handle back/forward navigation (bfcache)
// runOnPageLoad() does not need to fire when the browser's back/forward nav
// buttons are used, but we do need to record the adobeAnalyticsPageLoad()
window.addEventListener('pageshow', event => {
  if (event.persisted) {
    adobeAnalyticsPageLoad();
  }
});

function runOnPageLoad() {
  threeformSession.bootstrapThreeformSession();

  const isBot = /bot|crawl|spider|slurp|bingpreview|headlesschrome/i.test(
    navigator.userAgent
  );
  if (!!window.SentryAPI?.dsn && !isBot) {
    Sentry.init({ dsn: window.SentryAPI.dsn });
  }

  // Object fit images polyfill for IE9, IE10, IE11, Edge, Safari
  objectFitImages();

  // Navigations init
  inPageNav.init('js-in-page-nav', [
    'overview',
    'design',
    'products',
    'swatches',
    'color-location',
  ]);

  globalNavDesktop.init();

  // Contact A Rep / Find A Rep forms
  formLabelAnimation.init();
  formFindRep.init();
  formContactRep.init();

  //init accessible cards
  window.initAccessibleCards();
  window.threeUpSwiper = threeUpSwiper;
  // Carousels init
  if (document.querySelector('.js-swiper')) {
    fullSwiper.init('.js-swiper');
  }
  if (document.querySelector('.js-swiper-3up')) {
    threeUpSwiper.init('.js-swiper-3up');
  }
  if (document.querySelector('.js-swiper-2up')) {
    twoUpSwiper.init('.js-swiper-2up');
  }
  if (document.querySelector('.js-swiper-hero')) {
    heroSwiper.init('.js-swiper-hero', '.js-swiper-hero-content');
  }
  if (document.querySelector('.js-swiper-materials')) {
    materialsSwiper.init('.js-swiper-materials');
  }
  if (document.querySelector('.js-swiper-swatches')) {
    swatchSwiper.init('.js-swiper-swatches');
  }
  if (document.querySelector('.js-swiper-videos')) {
    videoSwiper.init('.js-swiper-videos');
  }
  if (document.querySelector('.js-swiper-content-block')) {
    contentBlockSwiper.init('.js-swiper-content-block');
  }
  if (document.querySelector('.js-swiper-image-gallery')) {
    imageGallerySwiper.init('.js-swiper-image-gallery');
  }

  const bitreelDigitalPrint = document.querySelector('#bitreel-digital-print');
  if (bitreelDigitalPrint) {
    setupDigitalPrintEventListener.init();
  }

  const bitreelConfigurator = document.querySelector('#bitreel-visualizer');
  if (bitreelConfigurator) {
    setupVisualizationEventListener.init();
  }

  adobeAnalyticsPageLoad();

  // Image Grid init
  imageGrid.init();

  // Modal Panels
  modalPanel.init();

  // Filters Init
  filter.init();

  document.addEventListener('click', showWebChatOnClick);

  //only applicable to DESKTOP
  //TODO on resize initialize
  const plpHeroNav = document.getElementById('js-hero-nav');
  if (plpHeroNav && !isMobileOrTablet()) resetDropdown('#main', '#js-hero-nav');

  //interlayer/graphic pattern plp
  const pdpVariants = document.querySelector('.js-variant-list');
  if (pdpVariants && isMobileOrTablet()) {
    const pdpVariantsTrigger = pdpVariants.querySelector('.accordion__trigger');
    const pdpVariantsContent = pdpVariants.querySelector('.accordion__content');
    //close the accordion
    if (hasClass(pdpVariantsTrigger, 'is-active')) {
      removeClass(pdpVariantsTrigger, 'is-active');
      removeClass(pdpVariantsContent, 'is-active');
    }
  }

  // Accessible tooltips helper
  // https://inclusive-components.design/tooltips-toggletips/
  const tooltips = document.querySelectorAll('[data-tooltip-content]');
  [].forEach.call(tooltips, tooltip => {
    const message = tooltip.getAttribute('data-tooltip-content');
    const liveRegion = tooltip.nextElementSibling;
    function showTooltip() {
      liveRegion.innerHTML = '';
      window.setTimeout(() => {
        liveRegion.innerHTML = `<span class="is-active">${message}</span>`;
      }, 100);
    }
    tooltip.addEventListener('click', showTooltip);
    tooltip.addEventListener('mouseenter', showTooltip);
    tooltip.addEventListener('mouseleave', function() {
      liveRegion.innerHTML = '';
    });
    document.addEventListener('click', e => {
      if (tooltip !== e.target) liveRegion.innerHTML = '';
    });
    tooltip.addEventListener('keydown', e => {
      if ((e.keyCode || e.which) === 27) liveRegion.innerHTML = '';
    });
  });

  // Animate <progress> helper
  const bars = Array.from(document.querySelectorAll('progress'));
  const ratio = [0, 25, 50, 75, 100];
  bars.forEach(bar => {
    const barValue = bar.dataset.propertyBarValue;
    const barContainer = bar.parentNode.parentNode;
    const toolTip = barContainer.querySelector('.button__tooltip');
    bar.setAttribute('value', ratio[barValue]);
    bar.style.setProperty('--value', `${ratio[barValue]}%`);
    // disable properties that have no assigned value
    if (barValue === '0') {
      barContainer.classList.add('is-disabled');
      toolTip.setAttribute('tabindex', -1);
    }
  });

  if (document.getElementById('js-exploded-interlayer')) {
    explodedInterlayerFamily.render({
      id: 'js-exploded-interlayer',
      index: 0,
      end: 6,
      interlayerState,
    });
  }

  function handleFirstTab(e) {
    if (e.keyCode === 9) {
      document.body.classList.add('user-is-tabbing');

      window.removeEventListener('keydown', handleFirstTab);
      window.addEventListener('mousedown', handleMouseDownOnce);
    }
  }

  function handleMouseDownOnce() {
    document.body.classList.remove('user-is-tabbing');

    window.removeEventListener('mousedown', handleMouseDownOnce);
    window.addEventListener('keydown', handleFirstTab);
  }

  window.addEventListener('keydown', handleFirstTab);

  multipleFileUpload.init();

  contestSubmissionModals.init();

  trackEmbeddedVideos();

  // Styles select elements in a non-OS-specific way
  prettySelect.init();

  sampleTypesSelect.init();
  setupAccountInformationSync();
  spreeIntegration();
  loadAccountInformation();
}

let Vue;
if (typeof Vue === 'undefined') Vue = window.Vue = require('vue').default;

Vue.filter('capitalizeAll', function(value) {
  if (!value) return '';
  return value.toString().toUpperCase();
});

Vue.component('icon-base', require('./vue/icons/IconBase.vue').default);
Vue.component(
  'icon-dropdown-arrow',
  require('./vue/icons/IconDropdownArrow.vue').default
);

const librisSearchNode = document.getElementById('libris-search-app');
if (librisSearchNode) {
  Vue.component(
    'libris-search',
    require('./vue/libris-search/LibrisSearch.vue').default
  );

  const librisSearchVue = new Vue({
    el: `#libris-search-app`,
  });
}

if (document.getElementById('resources-index-container')) {
  import(/* webpackChunkName: "samples" */ './react/pages/samples');
}

if (document.getElementById('tech-specs-container')) {
  import(/* webpackChunkName: "tech-specs" */ './react/pages/tech_specs');
}

if (document.getElementById('graphic-patterns-catalog-container')) {
  import(
    /* webpackChunkName: "graphic-patterns" */ './react/pages/graphic-patterns'
  );
}

if (document.getElementById('image-gallery-catalog-container')) {
  import(/* webpackChunkName: "image-gallery" */ './react/pages/image-gallery');
}

const galleryFilterNode = document.getElementById('gallery-index-container');
if (galleryFilterNode) {
  Vue.component(
    'gallery-index',
    require('./vue/gallery/GalleryIndex.vue').default
  );

  const galleryListVue = new Vue({ el: '#gallery-index-container' });
}
